import Link from 'next/link';
import React from 'react';

import {
  getOfferingPackageCoachingTimeLabel,
  getOfferingPackagePriceLabel,
  getPackageListingPageUrl,
  getPlanCustomerPageUrl,
} from '../../utils/package';

import { OfferingPackageImage } from './OfferingPackageImage';
import type {
  OfferingPackageCardCoachFragment,
  OfferingPackageCardOfferingPackageFragment,
} from './__generated-gql-types__/OfferingPackageCard.generated';

interface OfferingPackageCardProps {
  offeringPackage: OfferingPackageCardOfferingPackageFragment;
  onClick?: () => void;
  opportunityCoach: Possible<OfferingPackageCardCoachFragment>;
}

const OfferingPackageCard: React.FC<OfferingPackageCardProps> = ({
  offeringPackage,
  opportunityCoach,
  onClick,
}) => {
  const offeringPackageCoachingTimeLabel = getOfferingPackageCoachingTimeLabel(
    offeringPackage.prices,
  );

  return (
    <Link
      href={
        offeringPackage.isUniversalPackage
          ? getPlanCustomerPageUrl({
              categorySlug: offeringPackage.category?.slug,
              packageSlug: offeringPackage.slug,
              coachId: opportunityCoach?.id,
              coachTier: opportunityCoach?.tier,
            })
          : getPackageListingPageUrl({
              coachSlug: offeringPackage.coach.slug,
              packageSlug: offeringPackage.slug,
              packageId: offeringPackage.id,
            })
      }
      className="flex flex-col items-start justify-start border-b border-leland-gray-stroke py-6 md:rounded-md md:border md:py-0 md:shadow-sm md:hover:bg-leland-gray-hover"
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      id="offering_package_card--coach_package_link"
    >
      <div className="flex w-full grow items-center justify-start space-x-3 md:flex-col md:items-start md:space-x-0">
        <OfferingPackageImage
          containerClassName="shrink-0 md:w-full w-32"
          className="rounded-md md:rounded-b-none"
          name={offeringPackage.name}
          imageLink={offeringPackage.imageLink}
          sizes={{ lg: '576px', dflt: '128px' }}
        />
        <div className="flex flex-col items-start justify-between md:p-4">
          <h2 className="line-clamp-2 text-lg font-medium leading-snug text-leland-gray-dark">
            {offeringPackage.name}
          </h2>
          <p className="mt-2 line-clamp-2 text-leland-gray-light">
            {offeringPackage.subTitle}
          </p>
          <p className="mt-2 line-clamp-1 text-leland-gray-extra-light">
            {getOfferingPackagePriceLabel(offeringPackage.prices)}
            {offeringPackageCoachingTimeLabel ? (
              <>
                <span className="mx-2 my-1 inline-flex size-0.5 rounded-full bg-leland-gray-light" />
                <span>{offeringPackageCoachingTimeLabel}</span>
              </>
            ) : null}
          </p>
        </div>
      </div>
    </Link>
  );
};

export default OfferingPackageCard;
